import { z } from "zod";
import type { Operation } from "../types";

// Passkey auth contract (multi-tenant Phase 1). WebAuthn options + the browser's
// attestation/assertion are deeply-nested ceremony blobs validated by
// @simplewebauthn on the server, so the wire schema is a permissive object.
const webauthnBlob = z.record(z.string(), z.unknown());

const registerOptionsBody = z.object({
  handle: z.string().min(1).max(64),
  displayName: z.string().min(1).max(120),
});
type RegisterOptionsBody = z.infer<typeof registerOptionsBody>;

const optionsResponse = z.object({ options: webauthnBlob });
type OptionsResponse = z.infer<typeof optionsResponse>;

const registerVerifyBody = z.object({
  handle: z.string().min(1).max(64),
  response: webauthnBlob,
});
type RegisterVerifyBody = z.infer<typeof registerVerifyBody>;

const sessionResponse = z.object({
  userId: z.number(),
  handle: z.string(),
  // Present ONLY on a user's first registration: the one-time recovery codes to
  // save. Shown once, never returned again. Absent on login / device-add.
  recoveryCodes: z.array(z.string()).optional(),
});
type SessionResponse = z.infer<typeof sessionResponse>;

const recoverOptionsBody = z.object({
  handle: z.string().min(1).max(64),
  recoveryCode: z.string().min(1).max(128),
});
type RecoverOptionsBody = z.infer<typeof recoverOptionsBody>;

const recoverVerifyBody = z.object({
  handle: z.string().min(1).max(64),
  response: webauthnBlob,
});
type RecoverVerifyBody = z.infer<typeof recoverVerifyBody>;

const loginOptionsBody = z.object({ handle: z.string().max(64).optional() });
type LoginOptionsBody = z.infer<typeof loginOptionsBody>;

const loginVerifyBody = z.object({ response: webauthnBlob });
type LoginVerifyBody = z.infer<typeof loginVerifyBody>;

const okResponse = z.object({ ok: z.boolean() });
type OkResponse = z.infer<typeof okResponse>;

const meResponse = z.object({
  authenticated: z.boolean(),
  userId: z.number().nullable(),
  handle: z.string().nullable(),
  // null when an authenticated user belongs to no workspace (onboarding); the
  // single-player/anonymous fallback still reports workspace 1. See
  // excellent/src/projects/tenant-core.ts (TenantContext.workspaceId).
  workspaceId: z.number().nullable(),
});
type MeResponse = z.infer<typeof meResponse>;

const me: Operation<void, void, void, MeResponse> = {
  method: "GET",
  path: () => "/api/auth/me",
  response: meResponse,
};

// invalidation-ignore: write-nothing POST — issues a WebAuthn challenge only
const registerOptions: Operation<void, void, RegisterOptionsBody, OptionsResponse> = {
  method: "POST",
  path: () => "/api/auth/register/options",
  body: registerOptionsBody,
  response: optionsResponse,
};

const registerVerify: Operation<void, void, RegisterVerifyBody, SessionResponse> = {
  method: "POST",
  path: () => "/api/auth/register/verify",
  body: registerVerifyBody,
  response: sessionResponse,
  // Establishes a session — me flips to authenticated.
  invalidates: [me],
};

// invalidation-ignore: write-nothing POST — issues a WebAuthn challenge only
const loginOptions: Operation<void, void, LoginOptionsBody, OptionsResponse> = {
  method: "POST",
  path: () => "/api/auth/login/options",
  body: loginOptionsBody,
  response: optionsResponse,
};

const loginVerify: Operation<void, void, LoginVerifyBody, SessionResponse> = {
  method: "POST",
  path: () => "/api/auth/login/verify",
  body: loginVerifyBody,
  response: sessionResponse,
  invalidates: [me],
};

const logout: Operation<void, void, void, OkResponse> = {
  method: "POST",
  path: () => "/api/auth/logout",
  response: okResponse,
  invalidates: [me],
};

// Account recovery — register a NEW passkey using a one-time recovery code as
// proof of ownership (for a lost device). options validates the code + issues a
// challenge; verify re-checks, CONSUMES the code, and adds the credential.
// invalidation-ignore: write-nothing POST — validates recovery code + issues challenge only
const recoverOptions: Operation<void, void, RecoverOptionsBody, OptionsResponse> = {
  method: "POST",
  path: () => "/api/auth/recover/options",
  body: recoverOptionsBody,
  response: optionsResponse,
};

const recoverVerify: Operation<void, void, RecoverVerifyBody, SessionResponse> = {
  method: "POST",
  path: () => "/api/auth/recover/verify",
  body: recoverVerifyBody,
  response: sessionResponse,
  invalidates: [me],
};

// Regenerate recovery codes — re-mint the signed-in user's one-time codes,
// invalidating any prior unconsumed set. Session-gated; returns the new codes
// exactly once. Lets a user who used some codes top back up (no slow lockout).
const regenerateRecoveryCodesResponse = z.object({
  recoveryCodes: z.array(z.string()),
});
type RegenRecoveryResponse = z.infer<typeof regenerateRecoveryCodesResponse>;
// invalidation-ignore: recovery codes are not part of the me payload — one-shot return only
const regenerateRecoveryCodes: Operation<void, void, void, RegenRecoveryResponse> = {
  method: "POST",
  path: () => "/api/auth/recovery-codes",
  response: regenerateRecoveryCodesResponse,
};

export const auth = {
  registerOptions,
  registerVerify,
  loginOptions,
  loginVerify,
  logout,
  me,
  recoverOptions,
  recoverVerify,
  regenerateRecoveryCodes,
};
